import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Check, Shuffle } from "lucide-react";
import { api } from "@/lib/api";
import { useText } from "@/lib/useText";

function shuffle(list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const price = (p) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: p.currency || "EUR", maximumFractionDigits: 2 }).format(p.price || 0);

export function useStorePackages() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/store/packages").then(({ data: d }) => alive && setData(d)).catch(() => alive && setData({ packages: [], error: "unavailable" }));
    return () => { alive = false; };
  }, []);
  return data;
}

export function VipSpotlight({ packages, intervalMs = 8000, compact = false }) {
  const t = useText();
  const vips = useMemo(() => shuffle(packages.filter((p) => /vip/i.test(p.category) || /vip/i.test(p.tagline))), [packages]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (vips.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % vips.length), intervalMs);
    return () => clearInterval(t);
  }, [vips.length, intervalMs]);

  if (!vips.length) return null;
  const v = vips[index % vips.length];

  return (
    <article key={v.id} className={`card-eden relative overflow-hidden animate-fade-up ${compact ? "p-5" : "p-6 sm:p-8"}`} aria-live="polite" data-testid="vip-spotlight">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">{t("vip_spotlight")}</p>
          <h3 className="font-display text-2xl font-bold tracking-tight text-eden-text sm:text-3xl" data-testid="vip-spotlight-name">VIP {v.name}</h3>
          {v.tagline && <p className="mt-1 text-sm text-eden-muted">{v.tagline}</p>}
        </div>
        {v.image && <img src={v.image} alt="" className="h-16 w-16 shrink-0 rounded-sm object-contain sm:h-20 sm:w-20" loading="lazy" />}
      </div>
      {v.perks.length > 0 && (
        <ul className="mt-5 space-y-2">
          {v.perks.slice(0, compact ? 3 : 5).map((perk) => (
            <li key={perk} className="flex items-start gap-2 text-sm text-eden-secondary"><Check size={14} className="mt-0.5 shrink-0 text-eden-turquoise" aria-hidden="true" />{perk}</li>
          ))}
        </ul>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-xl font-bold text-eden-text" data-testid="vip-spotlight-price">{price(v)}</p>
        <div className="flex items-center gap-2">
          {vips.length > 1 && (
            <button type="button" onClick={() => setIndex((i) => (i + 1) % vips.length)} className="btn-ghost h-9 px-3" aria-label="Ver outro VIP" data-testid="vip-spotlight-next"><Shuffle size={14} /> {t("vip_other")}</button>
          )}
          <a href={v.url} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-eden-border px-3 text-sm font-medium text-eden-text transition-colors hover:border-eden-orange hover:text-eden-orange" data-testid="vip-spotlight-link">
            {t("vip_open")} <ArrowUpRight size={14} aria-hidden="true" />
          </a>
        </div>
      </div>
    </article>
  );
}

export function PackagesGrid({ packages }) {
  const t = useText();
  const vips = packages.filter((p) => /vip/i.test(p.category));
  const others = packages.filter((p) => !/vip/i.test(p.category));
  const Group = ({ title, items }) => items.length > 0 && (
    <div>
      <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-eden-muted">{title}</h3>
      <ul className="mt-4 grid gap-px overflow-hidden rounded-md border border-eden-border bg-eden-border sm:grid-cols-2 lg:grid-cols-4">
        {items.map((p) => (
          <li key={p.id} className="bg-eden-surface p-5 transition-colors hover:bg-eden-elevated" data-testid={`package-${p.id}`}>
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="block">
              <div className="flex items-center justify-between gap-3">
                <p className="font-display text-lg font-semibold text-eden-text">{/vip/i.test(p.category) ? `VIP ${p.name}` : p.tagline || p.name}</p>
                {p.image && <img src={p.image} alt="" className="h-10 w-10 rounded-sm object-contain" loading="lazy" />}
              </div>
              <p className="mt-3 font-mono text-sm text-eden-orange">{price(p)}</p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <div className="space-y-10" data-testid="packages-grid">
      <Group title={t("group_vips")} items={vips} />
      <Group title={t("group_others")} items={others} />
    </div>
  );
}
